import { useState } from 'react'
import { format } from 'date-fns'
import { TZDate } from '@date-fns/tz'
import { timestampDate, timestampFromDate, type Timestamp } from '@bufbuild/protobuf/wkt'

import { formatTimestamp } from '@/lib/date-utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/** A shift as the API stores it: when it starts and how long, in hours (fractional). */
export interface Shift {
  scheduledAt: Timestamp
  duration: number
}

/** What the operator types: wall-clock "HH:mm" times on the shift's day, in the event's zone. */
interface ShiftTimes {
  start: string
  end: string
}

export function shiftEnd(shift: Shift): Date {
  return new Date(timestampDate(shift.scheduledAt).getTime() + Math.round(shift.duration * 60) * 60_000)
}

/** "7:00 AM – 1:30 PM PDT": the zone suffix once, at the end. */
export function formatTimeRange(start: Date | Timestamp, end: Date | Timestamp, timeZone?: string): string {
  let from = formatTimestamp(start, 'h:mm a', timeZone)
  const to = formatTimestamp(end, 'h:mm a', timeZone)
  const zone = to.split(' ').slice(2).join(' ')
  if (zone && from.endsWith(` ${zone}`)) from = from.slice(0, -(zone.length + 1))
  return `${from} – ${to}`
}

function zoneOf(timeZone?: string): string {
  return timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** Wall-clock "HH:mm" of an instant in the zone, as a time input wants it. */
function wallClock(date: Date, timeZone?: string): string {
  return format(new TZDate(date.getTime(), zoneOf(timeZone)), 'HH:mm')
}

/**
 * The shift the operator asked for: the given wall-clock times on the current
 * shift's day in the event's zone. Undefined when a time is missing or the end
 * is not after the start, so a shift never crosses midnight here.
 */
export function shiftFromTimes(current: Shift, times: ShiftTimes, timeZone?: string): Shift | undefined {
  const zone = zoneOf(timeZone)
  const day = new TZDate(timestampDate(current.scheduledAt).getTime(), zone)
  const at = (hhmm: string): Date | undefined => {
    const match = /^(\d{2}):(\d{2})$/.exec(hhmm)
    if (!match) return undefined
    const wall = new TZDate(day.getFullYear(), day.getMonth(), day.getDate(), Number(match[1]), Number(match[2]), zone)
    return new Date(wall.getTime())
  }
  const start = at(times.start)
  const end = at(times.end)
  if (!start || !end) return undefined
  const minutes = Math.round((end.getTime() - start.getTime()) / 60_000)
  if (minutes <= 0) return undefined
  return { scheduledAt: timestampFromDate(start), duration: minutes / 60 }
}

interface StaffShiftEditorProps {
  name: string
  shift: Shift
  timeZone?: string
  disabled?: boolean
  /** Resolves true once saved; the editor then shows the shift again. */
  onSave: (shift: Shift) => Promise<boolean>
}

/**
 * One assigned person's shift under their name in the Assign Staff sheet:
 * "7:00 AM – 1:30 PM PDT" with an Edit that swaps in start and end time
 * inputs. Only the times change; the day stays the event's.
 */
export function StaffShiftEditor({ name, shift, timeZone, disabled, onSave }: StaffShiftEditorProps) {
  const [draft, setDraft] = useState<ShiftTimes | null>(null)
  const [saving, setSaving] = useState(false)
  const start = timestampDate(shift.scheduledAt)
  const end = shiftEnd(shift)

  if (!draft) {
    return (
      <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
        <span>{formatTimeRange(start, end, timeZone)}</span>
        <button
          type="button"
          className="text-primary underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50"
          disabled={disabled}
          onClick={() => setDraft({ start: wallClock(start, timeZone), end: wallClock(end, timeZone) })}
          aria-label={`Edit ${name}'s shift`}
        >
          Edit
        </button>
      </div>
    )
  }

  const next = shiftFromTimes(shift, draft, timeZone)

  const save = async () => {
    if (!next) return
    setSaving(true)
    const saved = await onSave(next)
    setSaving(false)
    if (saved) setDraft(null)
  }

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
      <div className="w-32">
        <Input
          type="time"
          aria-label={`${name}'s shift start`}
          value={draft.start}
          onChange={(e) => setDraft({ ...draft, start: e.target.value })}
        />
      </div>
      <span className="text-muted-foreground">–</span>
      <div className="w-32">
        <Input
          type="time"
          aria-label={`${name}'s shift end`}
          value={draft.end}
          onChange={(e) => setDraft({ ...draft, end: e.target.value })}
        />
      </div>
      <Button
        type="button"
        size="sm"
        disabled={!next || saving || disabled}
        onClick={() => void save()}
        aria-label={`Save ${name}'s shift`}
      >
        {saving ? 'Saving…' : 'Save'}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={saving}
        onClick={() => setDraft(null)}
        aria-label={`Cancel editing ${name}'s shift`}
      >
        Cancel
      </Button>
      {!next && (
        <p role="alert" className="basis-full text-destructive">
          End must be after start.
        </p>
      )}
    </div>
  )
}
